import {PlainObject, XH} from '@xh/hoist/core';
import {FieldSpec, genDisplayName} from '@xh/hoist/data';
import {isEmpty, isNil, last} from 'lodash';
import {ChartSpec, ChartSpecAxis, ChartSpecCapabilities, ChartSpecSeries, ChartType, DEFAULT_CHART_SPEC} from '../../cmp/chartSpec/ChartSpecTypes';
import {DEFAULT_GRID_SPEC, GridSpec, GridSpecCapabilities, GridSpecColumn} from '../../cmp/gridSpec/GridSpecTypes';
import {FieldCatalog, FieldCatalogEntry, FieldDataType, FormatSpec} from '../../cmp/spec/SpecTypes';
import {ResearchDataset, ResearchDatasetField, ResearchDataSource, ResearchFieldUnit} from '../../data/ResearchTypes';

/**
 * Turns a research dataset - described by the backend's data catalog - into what a configurable grid
 * or chart needs: its field catalog, and a default GridSpec / ChartSpec for a widget that has none yet.
 * This is what lets a widget show any dataset without a definition written for it.
 */
export const TICKER_FIELD = 'ticker';

/** Columns a default grid starts with - the rest can be added in the configurator. */
const MAX_DEFAULT_COLUMNS = 12;
const MAX_DEFAULT_SERIES = 4;
const MAX_DEFAULT_CATEGORIES = 6;

export const researchGridCapabilities: GridSpecCapabilities = {
    grouping: 'flat',
    filtering: 'flat',
    totals: true,
    leafRows: false
};

//------------------------
// Field catalog
//------------------------
const UNIT_HINTS: Record<ResearchFieldUnit, string> = {
    currency: 'USD',
    price: 'USD price',
    percent: 'fraction (0.05 = 5%)',
    ratio: 'ratio',
    shares: 'shares',
    count: 'count'
};

const ACRONYMS = /\b(Eps|Ebitda|Ebit|Ttm|Dcf|Ev|Sga|Etf|Pe|Cik|Url|Roe|Roa|Sec)\b/g;

/** 'epsAvg' -> 'EPS Avg' */
export function fieldLabel(name: string): string {
    return genDisplayName(name).replace(ACRONYMS, s => s.toUpperCase());
}

export function fieldType(field: ResearchDatasetField): FieldDataType {
    switch (field.dataType) {
        case 'int':
        case 'long':
        case 'decimal':
            return 'number';
        case 'date':
        case 'dateTime':
            return 'date';
        case 'bool':
            return 'bool';
        default:
            return 'string';
    }
}

/** A number a chart can plot. */
export function isMeasure(field: ResearchDatasetField): boolean {
    return field.role === 'measure' && fieldType(field) === 'number';
}

function toCatalogEntry(field: ResearchDatasetField): FieldCatalogEntry {
    const type = fieldType(field);
    return {
        name: field.name,
        label: fieldLabel(field.name),
        type,
        unit: field.unit ? UNIT_HINTS[field.unit] : undefined,
        description: field.description,
        isDimension: type !== 'date' && !isMeasure(field)
    };
}

/** A transposed grid has one row per field of the dataset - see `transposeRows()`. */
const TRANSPOSED_CATALOG: FieldCatalog = [
    {name: 'field', label: 'Field', type: 'string', isDimension: true},
    {name: 'value', label: 'Value', type: 'string', description: "The field's value, formatted by its unit"},
    {name: 'description', label: 'Description', type: 'string'}
];

export function fieldCatalogFor(dataset: ResearchDataset, source: ResearchDataSource): FieldCatalog {
    return source.transpose ? TRANSPOSED_CATALOG : dataset.fields.map(toCatalogEntry);
}

/** Store config for a grid over the source's rows - always loaded whole, never updated, so ids need only be unique. */
export function storeConfigFor(catalog: FieldCatalog): {idSpec: () => string; fields: FieldSpec[]} {
    return {
        idSpec: () => XH.genId(),
        fields: catalog.map(f => ({name: f.name, type: f.type === 'number' ? 'number' : 'auto'}))
    };
}

//------------------------
// Formats
//------------------------
const MILLIONS: FormatSpec = {type: 'number', numberStyle: 'millions'};

function unitFormat(field: ResearchDatasetField): FormatSpec {
    switch (field.unit) {
        case 'currency':
            return MILLIONS;
        case 'price':
            return {type: 'number', numberStyle: 'price', precision: 2};
        case 'percent':
            return {type: 'number', numberStyle: 'percent', precision: 2};
        case 'ratio':
            return {type: 'number', numberStyle: 'plain', precision: 2};
        case 'shares':
        case 'count':
            return {type: 'number', numberStyle: 'quantity'};
    }

    if (field.dataType === 'date') return {type: 'date'};
    if (field.dataType === 'dateTime') return {type: 'date', dateStyle: 'dateTime'};
    if (field.dataType === 'bool') return {type: 'bool'};
    return field.dataType === 'decimal' ? {type: 'number', numberStyle: 'plain', precision: 2} : null;
}

/** Format of one value shown on its own - amounts scale to the value rather than to a column. */
export function valueFormat(field: ResearchDatasetField, value: any): FormatSpec {
    if (field.unit !== 'currency' || typeof value !== 'number') return unitFormat(field);

    const abs = Math.abs(value);
    if (abs >= 1e9) return {type: 'number', numberStyle: 'billions', precision: 2, prefix: '$', suffix: 'B'};
    if (abs >= 1e6) return {type: 'number', numberStyle: 'millions', precision: 2, prefix: '$', suffix: 'M'};
    return {type: 'number', numberStyle: 'plain', precision: 2, prefix: '$'};
}

//------------------------
// Default grid
//------------------------
export function defaultGridSpec(dataset: ResearchDataset, source: ResearchDataSource): GridSpec {
    return gridSpecFor(dataset, source);
}

/**
 * A grid of the given fields, in that order - or of the dataset's first fields when none are given.
 * The date leads a grid of dated rows whether listed or not.
 */
export function gridSpecFor(dataset: ResearchDataset, source: ResearchDataSource, fieldNames?: string[]): GridSpec {
    if (source.transpose) {
        return {
            ...DEFAULT_GRID_SPEC,
            rowBorders: true,
            stripeRows: false,
            columns: [
                {field: 'field', headerName: 'Field', flex: true},
                {field: 'value', headerName: 'Value', align: 'right', width: 130}
            ]
        };
    }

    const {timeField} = dataset,
        fields = dataset.fields.filter(f => f.name !== TICKER_FIELD),
        time = fields.filter(f => f.name === timeField);

    let columns: ResearchDatasetField[];
    if (isEmpty(fieldNames)) {
        const dimensions = fields.filter(f => f.name !== timeField && !isMeasure(f) && f.role !== 'timestamp'),
            rest = fields.filter(f => !time.includes(f) && !dimensions.includes(f));
        columns = [...time, ...dimensions, ...rest].slice(0, MAX_DEFAULT_COLUMNS);
    } else {
        const chosen = fieldNames.map(name => fields.find(f => f.name === name)).filter(Boolean);
        columns = source.latest || chosen.some(f => f.name === timeField) ? chosen : [...time, ...chosen];
    }

    return {
        ...DEFAULT_GRID_SPEC,
        columns: columns.map(defaultColumn),
        sortBy: columns.some(f => f.name === timeField) ? [{colId: timeField, sort: 'desc'}] : []
    };
}

export function defaultColumn(field: ResearchDatasetField): GridSpecColumn {
    const format = unitFormat(field),
        label = fieldLabel(field.name),
        ret: GridSpecColumn = {field: field.name, headerName: field.unit === 'currency' ? `${label} (MM)` : label};

    if (format) ret.format = format;
    ret.width = fieldType(field) === 'string' ? 160 : 120;
    return ret;
}

//------------------------
// Default chart
//------------------------
/** A single snapshot plots its fields as categories; anything else plots one point per row. */
export function chartCapabilitiesFor(source: ResearchDataSource): ChartSpecCapabilities {
    return {layouts: [source.latest ? 'categories' : 'series']};
}

/** Null when the dataset has nothing a chart could plot. */
export function defaultChartSpec(dataset: ResearchDataset, source: ResearchDataSource): ChartSpec {
    return chartSpecFor(dataset, source);
}

/**
 * A chart of the given measures - or of the dataset's first ones when none are given - and of the given
 * type. Null when there is nothing a chart could plot.
 */
export function chartSpecFor(dataset: ResearchDataset, source: ResearchDataSource, fieldNames?: string[], type?: ChartType): ChartSpec {
    const measures = dataset.fields.filter(isMeasure),
        chosen = (fieldNames ?? []).map(name => measures.find(f => f.name === name)).filter(Boolean);
    if (!measures.length) return null;

    // By default plot fields of one unit together - amounts next to ratios would flatten the ratios
    const sameUnit = measures.filter(f => f.unit === measures[0].unit),
        plotted = isEmpty(chosen) ? sameUnit.slice(0, source.latest ? MAX_DEFAULT_CATEGORIES : MAX_DEFAULT_SERIES) : chosen,
        primary = unitAxis(plotted[0]);

    if (source.latest) {
        return {
            ...DEFAULT_CHART_SPEC,
            type: type ?? 'column',
            layout: 'categories',
            legend: 'none',
            series: plotted.map(f => ({field: f.name})),
            axes: {x: {}, primary, secondary: {}}
        };
    }

    // Over time when the rows are dated, else across the rows' first describing field (e.g. country)
    const xField = dataset.timeField ?? dataset.fields.find(f => f.name !== TICKER_FIELD && !isMeasure(f))?.name;
    if (!xField) return null;

    // Chosen fields of another unit than the first go on the second axis - a pie or bar chart has only one
    const oneAxis = type === 'pie' || type === 'bar',
        other = oneAxis ? undefined : plotted.find(f => f.unit !== plotted[0].unit);

    return {
        ...DEFAULT_CHART_SPEC,
        type: type ?? (dataset.timeField ? 'line' : 'column'),
        xField,
        series: plotted.map(f => chartSeriesFor(f, plotted[0], oneAxis)),
        axes: {x: {}, primary, secondary: other ? unitAxis(other) : {}}
    };
}

/** A series for a field of a chart whose first series plots `first` - on the second axis when its unit differs. */
export function chartSeriesFor(field: ResearchDatasetField, first: ResearchDatasetField, oneAxis = false): ChartSpecSeries {
    return !oneAxis && first && field.unit !== first.unit ? {field: field.name, axis: 'secondary'} : {field: field.name};
}

/** A y axis for values of the field's unit. */
export function unitAxis(field: ResearchDatasetField): ChartSpecAxis {
    const format = unitFormat(field),
        ret: ChartSpecAxis = {};
    if (format) ret.format = format;
    if (field.unit === 'currency') ret.title = 'USD (MM)';
    return ret;
}

//------------------------
// Transposed rows
//------------------------
/**
 * The newest row as one grid row per field that has a value - for snapshots such as TTM metrics.
 * Only the source's `fields`, in that order, when it names them.
 */
export function transposeRows(dataset: ResearchDataset, rows: PlainObject[], fieldNames?: string[]): PlainObject[] {
    const row = last(rows);
    if (!row) return [];

    const fields = isEmpty(fieldNames) ? dataset.fields : fieldNames.map(name => dataset.fields.find(f => f.name === name)).filter(Boolean);
    return fields
        .filter(f => f.name !== TICKER_FIELD && !isNil(row[f.name]))
        .map(f => ({
            name: f.name,
            field: fieldLabel(f.name),
            value: row[f.name],
            description: f.description
        }));
}
